import { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '@/hooks/useAuth';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  FadeIn,
  BouncyButton,
  AnimatedField,
  FloatingElement,
  LoadingDots,
  PulsingElement,
} from '@/components/AnimatedElements';
import { getConfig } from '@/config';
import { useThemeColors } from '@/styles';
import { logger } from '@/utils/logger';
import {
  showTransition,
  setLoginLogoRect,
  settleTransition,
} from '@/stores/loginTransitionStore';

interface LoginFormData {
  username: string;
  password: string;
  organization_code: string;
}

interface LoginFormProps {
  onLoginSuccess: () => void;
  onLoginFailure: () => void;
}

export default function LoginForm({ onLoginSuccess }: LoginFormProps) {
  const [errorMessage, setErrorMessage] = useState('');
  const { login, isLoading } = useAuth();
  const colors = useThemeColors();
  const logoRef = useRef<HTMLImageElement>(null);

  const config = getConfig();
  const webUrl = config.serviceWebUrl;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>();

  // Capture the login logo position for the merge animation
  const captureLogoRect = useCallback(() => {
    if (logoRef.current) {
      const rect = logoRef.current.getBoundingClientRect();
      setLoginLogoRect({
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
      });
    }
  }, []);

  // Focus username input after animations complete
  useEffect(() => {
    const timer = setTimeout(() => {
      const usernameInput = document.getElementById(
        'username'
      ) as HTMLInputElement;
      if (usernameInput) {
        usernameInput.focus();
      }
    }, 800); // Delay to allow all animations to complete
    return () => clearTimeout(timer);
  }, []);

  const onSubmit = async (data: LoginFormData) => {
    try {
      setErrorMessage('');

      // Capture logo position and show transition overlay BEFORE login
      captureLogoRect();
      showTransition();

      const result = await login(
        data.username,
        data.password,
        data.organization_code
      );
      if (result.success) {
        // Signal the overlay that login succeeded — it will animate to header
        settleTransition();
        onLoginSuccess();
      } else {
        // Signal error — overlay will dismiss
        settleTransition(result.message || 'Login failed');
        setErrorMessage(result.message || 'Login failed. Please try again.');
      }
    } catch (error) {
      logger.auth.error('Login error:', error);
      settleTransition('Something went wrong');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="w-full max-w-md">
      <Card
        className="shadow-2xl border-0 backdrop-blur-sm"
        style={{ backgroundColor: colors.offWhite + 'F2' }}
      >
        <CardHeader className="text-center pb-6">
          <FadeIn direction="down" delay={100} duration={0.6}>
            <div className="mx-auto flex justify-center">
              <FloatingElement delay={300} duration={4} amplitude={8}>
                <img
                  ref={logoRef}
                  src="/LearnBahn.png"
                  alt="LearnBahn Logo"
                  className="h-48 w-auto object-contain"
                />
              </FloatingElement>
            </div>
          </FadeIn>
        </CardHeader>

        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Username Field */}
            <FadeIn direction="up" delay={400} duration={0.5}>
              <AnimatedField isError={!!errors.username}>
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="Enter your username"
                    {...register('username', {
                      required: 'Username is required',
                      minLength: {
                        value: 3,
                        message: 'Username must be at least 3 characters',
                      },
                    })}
                    style={
                      errors.username
                        ? { borderColor: colors.error }
                        : undefined
                    }
                  />
                  {errors.username && (
                    <p className="text-sm" style={{ color: colors.error }}>
                      {errors.username.message}
                    </p>
                  )}
                </div>
              </AnimatedField>
            </FadeIn>

            {/* Password Field */}
            <FadeIn direction="up" delay={500} duration={0.5}>
              <AnimatedField isError={!!errors.password}>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 6,
                        message: 'Password must be at least 6 characters',
                      },
                    })}
                    style={
                      errors.password
                        ? { borderColor: colors.error }
                        : undefined
                    }
                  />
                  {errors.password && (
                    <p className="text-sm" style={{ color: colors.error }}>
                      {errors.password.message}
                    </p>
                  )}
                </div>
              </AnimatedField>
            </FadeIn>

            {/* Organization Code Field */}
            <FadeIn direction="up" delay={600} duration={0.5}>
              <AnimatedField isError={!!errors.organization_code}>
                <div className="space-y-2">
                  <Label htmlFor="organization_code">Organization Code</Label>
                  <Input
                    id="organization_code"
                    type="text"
                    placeholder="Enter 4-character code"
                    maxLength={4}
                    {...register('organization_code', {
                      required: 'Organization code is required',
                      minLength: {
                        value: 4,
                        message: 'Organization code must be 4 characters',
                      },
                      maxLength: {
                        value: 4,
                        message: 'Organization code must be 4 characters',
                      },
                      pattern: {
                        value: /^[A-Z0-9]{4}$/i,
                        message:
                          'Organization code must be 4 alphanumeric characters',
                      },
                    })}
                    style={
                      errors.organization_code
                        ? { borderColor: colors.error }
                        : undefined
                    }
                  />
                  <p
                    className="text-xs"
                    style={{ color: colors.text.secondary }}
                  >
                    Don't know your code?{' '}
                    <button
                      type="button"
                      className="font-medium"
                      style={{ color: colors.blue }}
                      onClick={() =>
                        setErrorMessage(
                          'Please contact your administrator to get your organization code.'
                        )
                      }
                    >
                      Contact Administrator
                    </button>
                  </p>
                  {errors.organization_code && (
                    <p className="text-sm" style={{ color: colors.error }}>
                      {errors.organization_code.message}
                    </p>
                  )}
                </div>
              </AnimatedField>
            </FadeIn>

            {/* Error Message */}
            {errorMessage && (
              <FadeIn direction="up" delay={100} duration={0.3}>
                <div
                  className="px-4 py-3 rounded-lg"
                  style={{
                    backgroundColor: colors.error + '10',
                    border: `1px solid ${colors.error}40`,
                    color: colors.error,
                  }}
                >
                  <p className="text-sm font-medium">{errorMessage}</p>
                </div>
              </FadeIn>
            )}

            {/* Submit Button */}
            <FadeIn direction="up" delay={700} duration={0.5}>
              <PulsingElement intensity="subtle" speed="slow">
                <BouncyButton
                  type="submit"
                  className="w-full py-3 px-6 rounded-lg shadow-lg hover:shadow-xl disabled:opacity-50"
                  style={{
                    background: `linear-gradient(to right, ${colors.primary}, ${colors.rust})`,
                    color: colors.text.inverse,
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <LoadingDots size="sm" />
                      <span className="ml-3">Signing In...</span>
                    </div>
                  ) : (
                    'Sign In'
                  )}
                </BouncyButton>
              </PulsingElement>
            </FadeIn>
          </form>

          {/* Additional Links */}
          <div className="text-center space-y-2">
            <div className="text-sm" style={{ color: colors.text.secondary }}>
              Don't have an account?{' '}
              <button className="font-medium" style={{ color: colors.blue }}>
                Contact Administrator
              </button>
            </div>
            <div className="text-xs" style={{ color: colors.text.secondary }}>
              <button style={{ color: colors.text.secondary }}>
                Forgot your password?
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Footer Note */}
      <div className="mt-8 text-center space-y-3">
        <div className="text-sm" style={{ color: colors.text.secondary }}>
          Secure access to your personalized learning environment
        </div>
        <div className="text-sm">
          <a
            href={webUrl}
            className="inline-flex items-center transition-colors font-medium"
            style={{ color: colors.blue }}
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to LearnBahn.com
          </a>
        </div>
      </div>
    </div>
  );
}
